import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  Calendar,
  Building2,
  TrendingUp,
  Eye,
  Trash2,
  MoreVertical,
  Loader2,
  RefreshCw,
  X
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { cn } from '@shared/utils/cn';

interface MatchHistoryProps {
  onSelectMatch?: (jobMatchId: string) => void;
}

interface JobMatch {
  id: string;
  job_title: string;
  company_name: string;
  match_score: number | null;
  created_at: string;
  updated_at: string;
}

interface FilterOptions {
  searchQuery: string;
  sortBy: 'date' | 'score' | 'company';
  sortOrder: 'asc' | 'desc';
  minScore: number | null;
}

export const MatchHistory: React.FC<MatchHistoryProps> = ({ onSelectMatch }) => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<JobMatch[]>([]);
  const [filteredMatches, setFilteredMatches] = useState<JobMatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    searchQuery: '',
    sortBy: 'date',
    sortOrder: 'desc',
    minScore: null
  });
  const [selectedMatch, setSelectedMatch] = useState<string | null>(null);

  useEffect(() => {
    loadMatches();
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [matches, filters]);

  const loadMatches = async () => {
    if (!user) return;

    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from('job_matches')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setMatches(data || []);
    } catch (error: any) {
      console.error('Error loading matches:', error);
      toast.error('Failed to load job matches');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...matches];

    if (filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        match =>
          match.job_title.toLowerCase().includes(query) ||
          match.company_name.toLowerCase().includes(query)
      );
    }

    if (filters.minScore !== null) {
      filtered = filtered.filter(
        match => match.match_score !== null && match.match_score >= filters.minScore!
      );
    }

    filtered.sort((a, b) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case 'date':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'score':
          const scoreA = a.match_score ?? -1;
          const scoreB = b.match_score ?? -1;
          comparison = scoreA - scoreB;
          break;
        case 'company':
          comparison = a.company_name.localeCompare(b.company_name);
          break;
      }

      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredMatches(filtered);
  };

  const deleteMatch = async (matchId: string) => {
    if (!confirm('Are you sure you want to delete this job match? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('job_matches')
        .delete()
        .eq('id', matchId);

      if (error) throw error;

      setMatches(prev => prev.filter(m => m.id !== matchId));
      toast.success('Job match deleted successfully');
    } catch (error: any) {
      console.error('Error deleting match:', error);
      toast.error('Failed to delete job match');
    }
  };

  const getScoreColor = (score: number | null) => {
    if (score === null) return 'bg-gray-100 text-gray-600';
    if (score >= 80) return 'bg-green-100 text-green-700';
    if (score >= 60) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const resetFilters = () => {
    setFilters({
      searchQuery: '',
      sortBy: 'date',
      sortOrder: 'desc',
      minScore: null
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-gray-600">Loading your job matches...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              Job Match History
            </h2>
            <p className="text-sm text-gray-600">
              {filteredMatches.length} {filteredMatches.length === 1 ? 'match' : 'matches'} found
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={loadMatches}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filters
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by job title or company..."
              value={filters.searchQuery}
              onChange={e =>
                setFilters(prev => ({ ...prev, searchQuery: e.target.value }))
              }
              className="pl-10"
            />
          </div>

          {showFilters && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort By
                  </label>
                  <select
                    value={filters.sortBy}
                    onChange={e =>
                      setFilters(prev => ({
                        ...prev,
                        sortBy: e.target.value as FilterOptions['sortBy']
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="date">Date</option>
                    <option value="score">Match Score</option>
                    <option value="company">Company Name</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort Order
                  </label>
                  <select
                    value={filters.sortOrder}
                    onChange={e =>
                      setFilters(prev => ({
                        ...prev,
                        sortOrder: e.target.value as FilterOptions['sortOrder']
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Score
                  </label>
                  <select
                    value={filters.minScore ?? ''}
                    onChange={e =>
                      setFilters(prev => ({
                        ...prev,
                        minScore: e.target.value ? Number(e.target.value) : null
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="">All Scores</option>
                    <option value="80">80% or higher</option>
                    <option value="60">60% or higher</option>
                    <option value="40">40% or higher</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button onClick={resetFilters} variant="outline" size="sm">
                  Reset Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {filteredMatches.length === 0 ? (
        <div className="bg-white rounded-xl border border-border/40 p-12 text-center">
          <div className="max-w-sm mx-auto">
            <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Matches Found
            </h3>
            <p className="text-gray-600 mb-4">
              {matches.length === 0
                ? "You haven't created any job matches yet. Start by uploading a job description to analyze."
                : 'Try adjusting your filters to see more results.'}
            </p>
            {matches.length > 0 && (
              <Button onClick={resetFilters} variant="outline">
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredMatches.map(match => (
            <div
              key={match.id}
              className={cn(
                'bg-white rounded-lg border transition-all',
                selectedMatch === match.id
                  ? 'border-primary shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              )}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {match.job_title}
                        </h3>
                        <p className="text-sm text-gray-600">{match.company_name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {new Date(match.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      {match.match_score !== null && (
                        <div className="flex items-center gap-1">
                          <TrendingUp className="h-4 w-4" />
                          <span
                            className={cn(
                              'px-2 py-0.5 rounded-full text-xs font-medium',
                              getScoreColor(match.match_score)
                            )}
                          >
                            {match.match_score}% Match
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {onSelectMatch && (
                      <Button
                        onClick={() => {
                          setSelectedMatch(match.id);
                          onSelectMatch(match.id);
                        }}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                    )}

                    <Button
                      onClick={() => deleteMatch(match.id)}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredMatches.length > 0 && (
        <div className="text-center text-sm text-gray-600">
          Showing {filteredMatches.length} of {matches.length} total matches
        </div>
      )}
    </div>
  );
};

export default MatchHistory;
